import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CanvasOrgChart from './CanvasOrgChart';
import API from '../services/API';

const StandaloneOrgChart = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const [treeData, setTreeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userInfo, setUserInfo] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);

  // Transform hierarchy data to match CanvasOrgChart expected format
  const transformHierarchyForCanvas = useCallback((hierarchyData, rootPhone, currentUserInfo = null) => {
    if (!hierarchyData || hierarchyData.length === 0) {
      // If no hierarchy data, create a single root node
      return [{
        id: rootPhone,
        name: currentUserInfo?.userName || currentUserInfo?.name || 'Root User',
        phone: rootPhone,
        level: 1,
        city: currentUserInfo?.city || currentUserInfo?.address || '',
        children: []
      }];
    }

    const transformNode = (node, level = 1) => {
      return {
        id: node.userCode || node.phone || node.id,
        name: node.userName || node.name || 'Unknown',
        phone: node.userCode || node.phone || '',
        level: level,
        city: node.city || node.address || '',
        children: node.children ? node.children.map(child => transformNode(child, level + 1)) : []
      };
    };

    // Transform hierarchy data
    return hierarchyData.map(node => transformNode(node, 1));
  }, []);

  const fetchOrgChartData = useCallback(async (phoneNumber) => {
    try {
      setLoading(true);
      setError('');
      setHasFetched(true);
      
      console.log('🚀 StandaloneOrgChart - Fetching org chart data for:', phoneNumber);

      // First check if user exists and get user info
      const userCheck = await API.checkUserExists(phoneNumber);
      console.log('👤 StandaloneOrgChart - User check result:', userCheck);

      if (!userCheck.exists) {
        throw new Error('Người dùng không tồn tại trong hệ thống');
      }

      setUserInfo(userCheck.userInfo);

      // Get subordinates hierarchy data
      const subordinatesResult = await API.getSubordinates(phoneNumber);
      console.log('🌳 StandaloneOrgChart - Subordinates result:', subordinatesResult);

      if (subordinatesResult.success) {
        const hierarchyData = subordinatesResult.hierarchy || [];
        console.log('🎯 StandaloneOrgChart - Processing hierarchy data:', hierarchyData);
        
        // Transform the hierarchy data for CanvasOrgChart
        const transformedData = transformHierarchyForCanvas(hierarchyData, phoneNumber, userCheck.userInfo);
        setTreeData(transformedData);
        console.log('✅ StandaloneOrgChart - Tree data set:', transformedData);
      } else {
        throw new Error('Không thể tải dữ liệu tổ chức');
      }
    } catch (error) {
      console.error('❌ StandaloneOrgChart - Error loading org chart:', error);
      setError(error.message || 'Có lỗi xảy ra khi tải sơ đồ tổ chức');
    } finally {
      setLoading(false);
    }
  }, [transformHierarchyForCanvas]);

  // Reset hasFetched when username changes
  useEffect(() => {
    setHasFetched(false);
  }, [username]);

  useEffect(() => {
    if (username && !hasFetched) {
      fetchOrgChartData(username);
    }
  }, [username, fetchOrgChartData, hasFetched]);

  const handleNodeClick = (node) => {
    setSelectedNode(node);
    console.log('🖱️ StandaloneOrgChart - Node clicked:', node);
  };

  const handleGoBack = () => {
    navigate('/to-chuc');
  };

  const handleRefresh = () => {
    if (username) {
      setHasFetched(false);
      fetchOrgChartData(username);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-50 to-brand-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Đang tải sơ đồ tổ chức</h3>
          <p className="text-gray-600">Đang lấy dữ liệu cho {username}...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.349 13.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Lỗi tải dữ liệu</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="space-y-3">
            <button
              onClick={handleRefresh}
              className="w-full px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors"
            >
              Thử lại
            </button>
            <button
              onClick={handleGoBack}
              className="w-full px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Quay lại
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 to-brand-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleGoBack}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span>Quay lại</span>
              </button>
              <div className="h-6 w-px bg-gray-300"></div>
              <h1 className="text-xl font-semibold text-gray-900">
                Sơ đồ tổ chức - {username}
              </h1>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleRefresh}
                className="px-3 py-2 text-sm bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>Làm mới</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6">
            {/* User Info */}
            {userInfo && (
              <div className="mb-6 p-4 bg-brand-50 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Thông tin người dùng</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Tên:</span>
                    <span className="ml-2 text-gray-900">{userInfo.userName || userInfo.name || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Số điện thoại:</span>
                    <span className="ml-2 text-gray-900">{username}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Cấp độ:</span>
                    <span className="ml-2 text-gray-900">{userInfo.level || 'N/A'}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Organization Chart */}
            <CanvasOrgChart
              treeData={treeData}
              onNodeClick={handleNodeClick}
              loading={false}
            />

            {/* Selected Node Info */}
            {selectedNode && (
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Thông tin node được chọn</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Tên:</span>
                    <span className="ml-2 text-gray-900">{selectedNode.name || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Số điện thoại:</span>
                    <span className="ml-2 text-gray-900">{selectedNode.phone || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Cấp độ:</span>
                    <span className="ml-2 text-gray-900">{selectedNode.level || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Thành phố:</span>
                    <span className="ml-2 text-gray-900">{selectedNode.city || 'N/A'}</span>
                  </div>
                </div>
                {selectedNode.children && selectedNode.children.length > 0 && (
                  <div className="mt-2">
                    <span className="font-medium text-gray-700">Số thành viên trực tiếp:</span>
                    <span className="ml-2 text-gray-900">{selectedNode.children.length}</span>
                  </div>
                )}
              </div>
            )}

            {/* Statistics */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-brand-50 p-4 rounded-lg">
                <h4 className="font-medium text-brand-900">Tổng số node</h4>
                <p className="text-2xl font-bold text-brand-600">
                  {treeData.length > 0 ? countAllNodes(treeData) : 0}
                </p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-medium text-green-900">Cấp độ tối đa</h4>
                <p className="text-2xl font-bold text-green-600">
                  {treeData.length > 0 ? getMaxLevel(treeData) : 0}
                </p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <h4 className="font-medium text-purple-900">Node gốc</h4>
                <p className="text-2xl font-bold text-purple-600">
                  {treeData.length}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper functions
const countAllNodes = (nodes) => {
  let count = 0;
  for (const node of nodes) {
    count++;
    if (node.children && node.children.length > 0) {
      count += countAllNodes(node.children);
    }
  }
  return count;
};

const getMaxLevel = (nodes, currentLevel = 1) => {
  let maxLevel = currentLevel;
  for (const node of nodes) {
    if (node.children && node.children.length > 0) {
      maxLevel = Math.max(maxLevel, getMaxLevel(node.children, currentLevel + 1));
    }
  }
  return maxLevel;
};

export default StandaloneOrgChart;
